/**
 * Sales Orders API
 *
 * API client for sales order operations
 */

import type {
  SalesOrder,
  CreateSalesOrderDto,
  UpdateSalesOrderDto,
  SalesOrderKpis,
  SalesOrderStatus,
  OrderLineItem,
  Priority,
} from '@lognai/plugin-orders';
import type { PaginationMetadata } from '@lognai/plugin-sdk';
import { apiFetch, buildQueryString, type ListQueryParams } from './client';

const BASE_URL = '/api/orders/sales-orders';

/**
 * Sales order domain filter fields
 */
export interface SalesOrderFilter {
  /** Free text over the order number and the counterparty (QA ledger ORD-006). */
  search?: string;
  status?: SalesOrderStatus;
  customerId?: string;
  warehouseId?: string;
  priority?: Priority;
  hasBackorders?: boolean;
  fromDate?: Date;
  toDate?: Date;
  /** Company custom fields marked filterable: `{ division: 'North', 'consignee.name': 'ACME' }`. */
  cf?: Record<string, string>;
}

/**
 * Sales order list query parameters (pagination + filters)
 */
export type SalesOrderFilterParams = ListQueryParams<SalesOrderFilter>;

/**
 * Paginated sales order list response
 */
export interface PaginatedSalesOrderList {
  items: SalesOrder[];
  pagination: PaginationMetadata | null;
}

/**
 * Stock status for a line item
 */
export interface LineItemStockStatus {
  lineItemId: string;
  productId: string;
  sku: string;
  productName: string;
  quantityOrdered: number;
  quantityAllocated: number;
  quantityPicked: number;
  quantityShipped: number;
  quantityBackordered: number;
  quantityRemaining: number;
  status: 'pending' | 'allocated' | 'picked' | 'shipped' | 'backorder' | 'partial';
}

/**
 * Stock status response for a sales order
 */
export interface SalesOrderStockStatus {
  orderId: string;
  orderNumber: string;
  overallStatus: 'pending' | 'allocated' | 'partial' | 'fulfilled' | 'backorder';
  totalItems: number;
  allocatedItems: number;
  backorderedItems: number;
  lineItems: LineItemStockStatus[];
}

/**
 * Get all sales orders with optional filters
 */
export const listSalesOrders = async (
  params?: SalesOrderFilterParams
): Promise<PaginatedSalesOrderList> => {
  const query = buildQueryString(params);
  return apiFetch<PaginatedSalesOrderList>(`${BASE_URL}${query}`);
};

/**
 * Get sales orders by customer
 */
export const getSalesOrdersByCustomer = async (
  customerId: string,
  params?: ListQueryParams
): Promise<PaginatedSalesOrderList> => {
  const query = buildQueryString(params);
  return apiFetch<PaginatedSalesOrderList>(
    `${BASE_URL}/by-customer/${customerId}${query}`
  );
};

/**
 * Get a single sales order by ID
 */
export const getSalesOrder = async (id: string): Promise<SalesOrder> => {
  return apiFetch<SalesOrder>(`${BASE_URL}/${id}`);
};

/**
 * Create a new sales order
 */
export const createSalesOrder = async (
  dto: CreateSalesOrderDto
): Promise<SalesOrder> => {
  return apiFetch<SalesOrder>(BASE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dto),
  });
};

/**
 * Update a sales order
 */
export const updateSalesOrder = async (
  id: string,
  dto: UpdateSalesOrderDto
): Promise<SalesOrder> => {
  return apiFetch<SalesOrder>(`${BASE_URL}/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dto),
  });
};

/**
 * Update sales order status
 */
export const updateSalesOrderStatus = async (
  id: string,
  status: SalesOrderStatus,
  reason?: string
): Promise<SalesOrder> => {
  return apiFetch<SalesOrder>(`${BASE_URL}/${id}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status, reason }),
  });
};

/**
 * Confirm a sales order (allocates stock)
 */
export const confirmSalesOrder = async (
  id: string,
  promisedDeliveryDate?: string,
  notes?: string
): Promise<SalesOrder> => {
  return apiFetch<SalesOrder>(`${BASE_URL}/${id}/confirm`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ promisedDeliveryDate, notes }),
  });
};

/**
 * Cancel a sales order (releases stock)
 */
export const cancelSalesOrder = async (
  id: string,
  reason?: string
): Promise<SalesOrder> => {
  return apiFetch<SalesOrder>(`${BASE_URL}/${id}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
};

/**
 * Fulfill a sales order
 */
export const fulfillSalesOrder = async (
  id: string,
  items?: Array<{
    lineItemId: string;
    quantity: number;
    warehouseId?: string;
    locationId?: string;
  }>,
  shipmentId?: string,
  notes?: string
): Promise<SalesOrder> => {
  return apiFetch<SalesOrder>(`${BASE_URL}/${id}/fulfill`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items, shipmentId, notes }),
  });
};

/**
 * Get sales order line items
 */
export const getSalesOrderLines = async (
  id: string
): Promise<OrderLineItem[]> => {
  return apiFetch<OrderLineItem[]>(`${BASE_URL}/${id}/lines`);
};

/**
 * Get stock status for sales order
 */
export const getSalesOrderStockStatus = async (
  id: string
): Promise<SalesOrderStockStatus> => {
  return apiFetch<SalesOrderStockStatus>(`${BASE_URL}/${id}/stock-status`);
};

/**
 * Get sales order KPIs
 */
export const getSalesOrderKpis = async (): Promise<SalesOrderKpis> => {
  return apiFetch<SalesOrderKpis>(`${BASE_URL}/kpis`);
};
